// Where Otto is looking.
//
// His pupils already move, and until now they only ever followed the cursor.
// This points them at things that matter instead, so a perched Otto looks like
// he is paying attention to your work rather than to your mouse.
//
// THE LINE THIS MUST NOT CROSS: attentive, not surveillance. An owl whose eyes
// track you continuously is unsettling, and targeted gaze is MORE legible than
// cursor drift — which is exactly what makes it creepy if overdone. Three rules
// hold the line, and all three are enforced here rather than left to callers:
//
//   · He looks, then stops. Every gaze expires; nothing is tracked forever.
//   · He looks at EVENTS, not at people. A row that changed, a panel that
//     appeared. Never a field somebody is typing into.
//   · He is often looking at nothing. Rest is the common state.
//
// If in doubt, look less.

// Measured from the OttoAlive SVG, not guessed. `.otto-pupils` carries the iris
// (r 7.5) as well as the pupil, and moves as one group inside the eye white
// (r 14). The irises sit 2px off-centre in opposite directions, so whichever
// way the group travels, one eye runs out of room first:
//
//   right → bound by the LEFT eye:  14 - (2 + 7.5)  = 4.5px
//   left  → bound by the RIGHT eye: 14 - (7.5 - 2)  = 4.5px  (mirrored)
//   up/down → irises are vertically centred:  14 - 7.5 = 6.5px
//
// Past these the iris edge leaves the white and it reads as a squint.
export const MAX_X = 4.5;
export const MAX_Y = 6.5;

// Ordinary targets use 80%, leaving the last 20% as headroom for a deliberate
// stare. Nothing currently claims that headroom — it exists so a future
// emphatic look has somewhere to go without touching the clip limit.
export const ORDINARY = 0.8;

// Nothing holds his eyes longer than this, whatever priority it claimed and
// whatever hold it asked for. A target that genuinely persists gets released
// anyway and can be re-acquired by a later event — which reads as glancing back,
// not as staring.
export const MAX_HOLD_MS = 4000;

export const PRIORITY = {
  CHANGED: 1,   // an element that just changed — a new row, an updated count
  SHOT: 2,      // the element a roaming shot is about to touch
  HOVER: 3,     // a hovered interactive row or card
};

// Default holds per priority. CHANGED is deliberately short: the interesting
// part of a change is that it happened, and two seconds is long enough to read
// as noticing without becoming a stare.
const DEFAULT_HOLD = { 1: 2000, 2: MAX_HOLD_MS, 3: 4000 };

let targets = [];          // [{ el, priority, id, expires }]
let nextId = 1;
const listeners = new Set();
let timer = null;

const now = () => Date.now();

// Lowest priority number wins; ties break to the most recently declared, so a
// second change during a hold moves his attention rather than being ignored.
const resolve = () => {
  const live = targets.filter(t => t.expires > now() && t.el && t.el.isConnected);
  if (live.length !== targets.length) targets = live;
  if (!live.length) return null;
  let best = live[0];
  for (const t of live) {
    if (t.priority < best.priority || (t.priority === best.priority && t.id > best.id)) best = t;
  }
  return best;
};

let lastNotified = null;
const notify = () => {
  const best = resolve();
  const el = best ? best.el : null;
  if (el === lastNotified) return;
  lastNotified = el;
  listeners.forEach((fn) => { try { fn(el); } catch (e) { /* a bad listener must not freeze his eyes */ } });
};

// One timer for the whole registry rather than one per target: targets are
// short-lived and usually few, and a stray per-target timer is exactly how a
// gaze gets stuck on after its element has gone.
const rearm = () => {
  if (timer) { clearTimeout(timer); timer = null; }
  const live = targets.filter(t => t.expires > now());
  if (!live.length) return;
  const soonest = Math.min(...live.map(t => t.expires)) - now();
  timer = setTimeout(() => { timer = null; notify(); rearm(); }, Math.max(16, soonest));
};

// Ask Otto to look at an element.
//
//   const stop = lookAt(rowEl, { priority: PRIORITY.CHANGED });
//
// Returns a release. Calling it early is the normal way to end a hover; letting
// it expire is the normal way to end everything else. Releasing twice is safe.
export function lookAt(el, { priority = PRIORITY.CHANGED, holdMs } = {}) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return () => {};
  const hold = Math.min(holdMs || DEFAULT_HOLD[priority] || 2000, MAX_HOLD_MS);
  const entry = { el, priority, id: nextId++, expires: now() + hold };
  targets.push(entry);
  notify();
  rearm();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    targets = targets.filter(t => t !== entry);
    notify();
    rearm();
  };
}

export function subscribeGaze(fn) {
  listeners.add(fn);
  // The immediate call needs the same guard as notify(). Without it a listener
  // that throws takes down whoever SUBSCRIBED rather than just itself — and the
  // subscriber here is an effect inside OttoAlive, so one bad listener would
  // break the component that renders the owl.
  try { fn(resolve()?.el || null); } catch (e) { /* see above */ }
  return () => listeners.delete(fn);
}

export function currentGazeTarget() {
  return resolve()?.el || null;
}

// Given Otto's own box and a target box, how far the pupils should travel.
// Pure, so the clamping can be tested at the extremes rather than by eye.
//
// The offset is a DIRECTION, not a distance: a target on the far side of the
// screen and one just outside his box both read as "over there". Normalising by
// a fixed radius rather than the actual distance keeps a near target from
// producing a tiny, hesitant-looking flick.
export function gazeOffset(ottoRect, targetRect, scale = ORDINARY) {
  if (!ottoRect || !targetRect) return { x: 0, y: 0 };
  const ox = ottoRect.left + ottoRect.width / 2;
  const oy = ottoRect.top + ottoRect.height / 2;
  const tx = targetRect.left + targetRect.width / 2;
  const ty = targetRect.top + targetRect.height / 2;
  const dx = tx - ox;
  const dy = ty - oy;
  const dist = Math.hypot(dx, dy);
  if (dist < 1) return { x: 0, y: 0 };
  // Full deflection once the target is a couple of Otto-widths away; closer
  // than that it scales down, so something right beside him is glanced at
  // rather than stared past.
  const reach = Math.max(120, ottoRect.width * 2);
  const strength = Math.min(1, dist / reach);
  return {
    x: clamp((dx / dist) * strength * MAX_X * scale, MAX_X),
    y: clamp((dy / dist) * strength * MAX_Y * scale, MAX_Y),
  };
}

function clamp(v, limit) {
  return Math.max(-limit, Math.min(limit, v));
}

const isTouch = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Spread onto anything already interactive to have Otto glance at it on hover:
//
//   <button {...gazeHover} …>
//
// A static object, not a factory, so spreading it across a list of rows costs
// nothing per row and never invalidates a memo. The release is keyed off the
// element, so two rows hovered in quick succession release independently
// instead of the second clearing the first's entry.
//
// Adds handlers to the ROW, never to Otto — he stays decorative and
// pointer-events:none, which is what stops him eating a click on New Brief.
const hoverReleases = new WeakMap();
export const gazeHover = {
  onMouseEnter: (e) => {
    if (isTouch() || reduced()) return;
    const el = e.currentTarget;
    const prev = hoverReleases.get(el);
    if (prev) prev();
    hoverReleases.set(el, lookAt(el, { priority: PRIORITY.HOVER }));
  },
  onMouseLeave: (e) => {
    const el = e.currentTarget;
    const release = hoverReleases.get(el);
    if (release) { release(); hoverReleases.delete(el); }
  },
};

// Test-only: module state outlives a test file otherwise.
export function __resetGaze() {
  targets = [];
  listeners.clear();
  lastNotified = null;
  if (timer) { clearTimeout(timer); timer = null; }
}
